import { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '@/lib/api';
import type { Notification } from '@/mocks/shared';

type StoreRole = 'admin' | 'organizer' | 'vendor';

const LAST_SEEN_KEYS: Record<StoreRole, string> = {
  admin: 'app_notifications_last_seen_admin_v1',
  organizer: 'app_notifications_last_seen_organizer_v1',
  vendor: 'app_notifications_last_seen_vendor_v1',
};

const formatRelativeTime = (value?: string) => {
  if (!value) return '';

  const diffMinutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);

  if (diffMinutes <= 0) return 'À l’instant';
  if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `Il y a ${diffHours}h`;

  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return 'Hier';
  return `Il y a ${diffDays} jours`;
};

export const getLastSeenDate = (role: StoreRole = 'organizer'): string =>
  localStorage.getItem(LAST_SEEN_KEYS[role]) ?? new Date(0).toISOString();

export const saveLastSeenDate = (role: StoreRole = 'organizer') => {
  try {
    localStorage.setItem(LAST_SEEN_KEYS[role], new Date().toISOString());
  } catch {
    // Ignore local storage issues for non-critical UX state.
  }
};

export const useNotificationsStore = (role: StoreRole = 'organizer') => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchNotifications = async () => {
    try {
      const response = await apiRequest<Array<{
        id: string;
        title: string;
        message: string;
        type: Notification['type'];
        read: boolean;
        date?: string;
      }>>('/notifications');

      setNotifications(
        response.map((notification) => ({
          ...notification,
          time: formatRelativeTime(notification.date),
        })),
      );
    } catch (error) {
      console.error(`Notifications ${role} error:`, error);
      setNotifications([]);
    }
  };

  useEffect(() => {
    void fetchNotifications();

    const intervalId = window.setInterval(() => {
      void fetchNotifications();
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [role]);

  const addNotification = async () => {
    await fetchNotifications();
  };

  const markAsRead = (id: string) => {
    setNotifications((prev) => prev.map((notification) => (
      notification.id === id ? { ...notification, read: true } : notification
    )));

    void apiRequest(`/notifications/${id}/read`, { method: 'POST' });
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
    void apiRequest('/notifications/read-all', { method: 'POST' });
  };

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications],
  );

  const newSinceLastSeen = useMemo(
    () => notifications.filter((notification) => (
      !notification.read && notification.date
        ? new Date(notification.date) > new Date(getLastSeenDate(role))
        : false
    )),
    [notifications, role],
  );

  return {
    notifications,
    addNotification,
    markAsRead,
    markAllAsRead,
    unreadCount,
    newSinceLastSeen,
    refreshNotifications: fetchNotifications,
  };
};
